import type { FC } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import c from 'classnames';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { getPointsCount } from '@wix/da-shared-react/pkg/publicSession/selectors';
import {
  getFreePromptsLimit,
  getFreePromptsRemaining,
} from '@wix/da-dreamup/pkg/redux/form/selectors';
import { pushPointsModal } from '@wix/da-dreamup/pkg/redux/form/actions';
import { SpendType } from '../useSpendType';
import s from './PromptCountAndPointsBalance.scss';

interface Props {
  spendType: SpendType;
}

function usePromptCountText(spendType: SpendType) {
  const { t } = useTranslation();
  const freePromptsRemaining = useSelector(getFreePromptsRemaining);
  const freePromptsLimit = useSelector(getFreePromptsLimit);

  switch (spendType) {
    case SpendType.UpgradeToCore:
    case SpendType.SubmitWithPrompt:
      return (
        <Trans
          i18nKey="dreamup.modal.spendConfirmation.promptCount"
          count={freePromptsRemaining}
          values={{
            limit: freePromptsLimit,
          }}
          components={{
            b: <b />,
          }}
        />
      );
    case SpendType.SubmitWithPoints:
    case SpendType.PurchasePoints:
    default:
      return t(
        'dreamup.modal.spendConfirmation.promptCount.noPromptsRemaining.core'
      );
  }
}

const PromptCountAndPointsBalance: FC<Props> = ({ spendType }) => {
  const { t } = useTranslation();
  const promptCountText = usePromptCountText(spendType);
  const pointsCount = useSelector(getPointsCount);
  const dispatchPushPointsModal = useAction(pushPointsModal);

  return (
    <div>
      <div className={s['prompt-count']}>{promptCountText}</div>
      {[SpendType.PurchasePoints, SpendType.SubmitWithPoints].includes(
        spendType
      ) && (
        <div className={s['points']}>
          {t('dreamup.modal.spendConfirmation.pointsBalance', {
            count: pointsCount,
          })}{' '}
          <PlainButtonOrLink
            onClick={dispatchPushPointsModal}
            className={c(s['get-more-link'], 'text-link-level-2-primary')}
          >
            {t('dreamup.modal.spendConfirmation.pointsBalance.buyButton')}
          </PlainButtonOrLink>
        </div>
      )}
    </div>
  );
};

PromptCountAndPointsBalance.displayName = 'PromptCountAndPointsBalance';

export default PromptCountAndPointsBalance;
